import { useEffect, useState, useRef } from 'react'
import { useSnackbar } from 'notistack5'
// material
import { Link as RouterLink } from 'react-router-dom'
import { Box, Grid, Container, Typography, CardHeader, Card, Button, TextField, Modal, IconButton, FormControl, Autocomplete } from '@material-ui/core'
// import { Visibility, VisibilityOff } from '@mui/icons-material'
import QRCode from 'react-qr-code'
import OTPInput from 'react-otp-input'
// components
import Page from '../components/Page'
import HeaderBreadcrumbs from 'components/HeaderBreadcrumbs'

import storage from 'utils/storage'
import user_api from 'utils/api/users'
import Bugsnag from '@bugsnag/js'
import { createUserLogger, ACTION_CATEGORIES } from 'utils/actionLogger'
// ----------------------------------------------------------------------

const style = { // Style for modal
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px'
};

export default function StoreQR() {
  const [user, setUser] = useState({})
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false)
  const [branches, setBranches] = useState([])
  const [stores, setStores] = useState([])
  const [selectedStore, setSelectedStore] = useState(null)
  const [timeInQR, setTimeInQR] = useState('')
  
  const [internalOtp, setInternalOtp] = useState('');
  const [internalOtpValidUntil, setInternalOtpValidUntil] = useState('');
  const [isInternalOtpLoading, setIsInternalOtpLoading] = useState(false);
  const [isQrGenerating, setIsQrGenerating] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isSendingEmailOtp, setIsSendingEmailOtp] = useState(false);

  const [openOTPDialog, setOpenOTPDialog] = useState(false); // For modal visibility
  const [otpFromModal, setOtpFromModal] = useState(''); // OTP input from modal
  const cooldownTimerRef = useRef(null);
  
  // Initialize logger for the current user
  const logger = user._id ? createUserLogger(user) : null;

  useEffect(() => {
    const load = async () => {
      setLoading(true); 
      try {
        const local_user = await storage.getUser()
        if (!local_user) return;
        const parsedUser = JSON.parse(local_user) 
        setUser(parsedUser)

        if (parsedUser._id) { 
            const branch = await user_api.get_user_branch(parsedUser._id);
            if (!branch.ok) {
            Bugsnag.notify(branch)
            enqueueSnackbar('Unable to fetch branches', { variant: 'warning' })
            } else {
            setBranches(branch.data)
            }

            // Load stores for employee registration selection (for role 3 users)
            if (parsedUser.role >= 3) {
              try {
                const storesResult = await user_api.get_group_store_v2(parsedUser._id);
                if (storesResult.status === 200 && storesResult.data.report.store) {
                  // Create user's own store option as first option
                  const ownStore = {
                    storeName: parsedUser.company || 'My Store',
                    adminId: parsedUser._id
                  };
                  
                  // Combine own store with other stores, ensuring own store is first
                  const allStores = [ownStore, ...storesResult.data.report.store];
                  
                  setStores(allStores);
                  // Set the first store (own store) as default selected store
                  setSelectedStore(ownStore);
                } else {
                  // If no group stores, just use own store
                  const ownStore = {
                    storeName: parsedUser.company || 'My Store',
                    adminId: parsedUser._id
                  };
                  setStores([ownStore]);
                  setSelectedStore(ownStore);
                }
              } catch (error) {
                console.error('Error loading stores:', error);
                // Fallback to own store on error
                const ownStore = {
                  storeName: parsedUser.company || 'My Store',
                  adminId: parsedUser._id
                };
                setStores([ownStore]);
                setSelectedStore(ownStore);
              }
            }

            // Attempt to fetch existing internal OTP
            if (parsedUser.role > 0) { // Only fetch if user is authorized (e.g., store owner/manager)
              try {
                const existingOtpResult = await user_api.get_existing_internal_timein_otp({ id: parsedUser._id });
                if (existingOtpResult.data && existingOtpResult.data.success) {
                  setInternalOtp(existingOtpResult.data.otp);
                  setInternalOtpValidUntil(new Date(existingOtpResult.data.validUntil).toLocaleString());
                  enqueueSnackbar('Existing valid OTP loaded.', { variant: 'info' });
                } else if (existingOtpResult.data && !existingOtpResult.data.success) {
                  // Optional: enqueueSnackbar(existingOtpResult.data.message, { variant: 'info' }); // e.g., "No active internal OTP found..."
                  console.log('No active internal OTP found for user:', existingOtpResult.data.message);
                }
              } catch (otpError) {
                Bugsnag.notify(otpError);
                console.error("Error fetching existing OTP:", otpError);
                // Don't show a user-facing error for this, as it's an enhancement
              }
            }
        }
        
        if (parsedUser.role > 0 && parsedUser.company) { 
          const qrResult = await user_api.get_timein_qr({ company: parsedUser.company })
          if (qrResult.ok && qrResult.data.QRCode) {
              setTimeInQR(qrResult.data.QRCode)
          }
        }
        
      } catch (error) {
        Bugsnag.notify(error)
        enqueueSnackbar('An error occurred while loading initial data', { variant: 'error' })
      } finally {
        setLoading(false)
      }
    }

    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGenerateInternalOTP = async () => {
    if (!user._id) {
      enqueueSnackbar('User information not loaded. Cannot generate OTP.', { variant: 'error' });
      return;
    }
    setIsInternalOtpLoading(true);
    try {
      const result = await user_api.generate_timein_otp({ id: user._id });
      if (result.data && result.data.success) {
        setInternalOtp(result.data.otp);
        setInternalOtpValidUntil(new Date(result.data.validUntil).toLocaleString());
        enqueueSnackbar(result.data.message || 'OTP generated successfully. Valid for 24 hours.', { variant: 'success' });
        // Log the successful OTP generation
        if (logger) {
          logger.logSuccess({
            action: 'Generated 24-Hour OTP',
            category: ACTION_CATEGORIES.OTP,
            details: 'Successfully generated a 24-hour OTP for schedule/premium approvals.'
          });
        }
      } else {
        Bugsnag.notify(result.data || result);
        enqueueSnackbar(result.data.message || 'Failed to generate OTP. Please check console.', { variant: 'error' });
        setInternalOtp('');
        setInternalOtpValidUntil('');
      }
    } catch (error) {
      Bugsnag.notify(error);
      enqueueSnackbar('An error occurred while generating OTP.', { variant: 'error' });
      setInternalOtp('');
      setInternalOtpValidUntil('');
    } finally {
      setIsInternalOtpLoading(false);
    }
  };

  const openOtpModalForQRGeneration = () => {
    handleRequestEmailOtpForQR();
    setOtpFromModal(''); // Clear previous modal input
    setOpenOTPDialog(true);
  };

  const handleCloseOTPDialog = () => {
    setOpenOTPDialog(false);
    setOtpFromModal('');
  };

  const handleModalSubmitAndGenerateQR = async () => {
    if (!user._id) {
      enqueueSnackbar('User information not loaded. Cannot generate QR.', { variant: 'error' });
      return;
    }

    if (otpFromModal.length !== 6) {
      enqueueSnackbar('Please enter a valid 6-digit OTP.', { variant: 'error' });
      return;
    }

    setIsQrGenerating(true);
    try {
      const result = await user_api.generate_timein_qr({ id: user._id, otp: otpFromModal });
      if (result.data && result.data.success) {
        setTimeInQR(result.data.QRCode);
        enqueueSnackbar(result.data.message || 'QR Code generated successfully!', { variant: 'success' });
        setOtpFromModal('');
        setOpenOTPDialog(false);
        // Log the successful QR code generation
        if (logger) {
          logger.logSuccess({
            action: 'Generated Schedule/Premium QR Code',
            category: ACTION_CATEGORIES.QR_CODE,
            details: 'Successfully generated a new QR code for schedule/premium approvals.'
          });
        }
      } else {
        Bugsnag.notify(result.data || result);
        enqueueSnackbar(result.data.message || 'Failed to generate QR Code. OTP might be invalid or expired.', { variant: 'error' });
      }
    } catch (error) {
      Bugsnag.notify(error);
      enqueueSnackbar('An error occurred while generating QR code.', { variant: 'error' });
    } finally {
      setIsQrGenerating(false);
    }
  };

  const handleRequestEmailOtpForQR = async () => {
    if (!user || !user._id) {
      enqueueSnackbar('User information not available. Cannot request OTP.', { variant: 'error' });
      return;
    }

    // Use the dedicated loading state for email OTP
    setIsSendingEmailOtp(true); 
    try {
      // Calls the API to send a 5-minute OTP to the user's email for QR code generation.
      const result = await user_api.send_timein_otp({ id: user._id }); 
      
      if (result.data && result.data.success) {
        enqueueSnackbar(result.data.message || 'OTP has been sent to your email. Please check your inbox.', { variant: 'success' });
        
        // Start the cooldown timer
        setResendCooldown(10);
        if (cooldownTimerRef.current) {
          clearInterval(cooldownTimerRef.current);
        }
        cooldownTimerRef.current = setInterval(() => {
          setResendCooldown(prev => {
            if (prev <= 1) {
              clearInterval(cooldownTimerRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        Bugsnag.notify(result.data || new Error('user_api.send_timein_otp call returned unsuccessful or no data.'));
        enqueueSnackbar(result.data.message || 'Failed to send OTP to your email. Please try again.', { variant: 'error' });
      }
    } catch (error) {
      Bugsnag.notify(error);
      enqueueSnackbar('An error occurred while sending the OTP email. Please try again.', { variant: 'error' });
    } finally {
      setIsSendingEmailOtp(false); 
    }
  };

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);

  const handleStoreChange = (event, value) => {
    if (value === selectedStore || value === null) return;
    setSelectedStore(value);
  };

  return (
    <>
    <Page title="QR Codes">
      <Container maxWidth="xl">
        <HeaderBreadcrumbs
          heading="Store QR Codes"
          links={[{ name: 'Dashboard', href: '/stores/app' }, { name: 'QR Code' }]}
        />
        <Box sx={{ pb: 5 }}>
          <Typography variant="h4">Employee Registration</Typography>
          This will redirect them to registration page for employees only!
        </Box>

        {/* Store Selection for Employee Registration */}
        {stores.length > 0 && user.role >= 3 && (
          <Box sx={{ pb: 3, mb: 3 }}>
            <FormControl fullWidth>
              <Autocomplete
                id="store-select-employee-registration"
                value={selectedStore === null ? stores[0] : selectedStore}
                onChange={handleStoreChange}
                options={stores}
                getOptionLabel={(option) => option?.storeName || ''}
                isOptionEqualToValue={(option, value) => option?.adminId === value?.adminId}
                renderInput={(params) => (
                  <TextField {...params} label="Select Store for Employee Registration" />
                )}
                sx={{ maxWidth: 400 }}
              />
            </FormControl>
          </Box>
        )}

        <Grid container spacing={3} sx={{ px: 3 }}>
          {user.role >= 3 ? (
          <Card sx={{ pb: 5, pl: 5, pr: 5, mb:3 }}>
            <CardHeader title={(user.role >= 3 && selectedStore?.storeName) || user.company || 'Your Company'} sx={{ pl: 0, pb: 0 }} />
            <Box sx={{ pl: 0, pt: 0, pb: 2 }}>
              <Typography variant="body1" gutterBottom sx={{ pl: 0, pt: 0, pb: 2 }}>
                {(user.role >= 3 && selectedStore?.adminId) || user._id || 'User ID'}
              </Typography>
            </Box>
            {((user.role >= 3 && selectedStore?.adminId) || user._id) && <QRCode value={`${process.env.REACT_APP_CLIENT_URL}/${(user.role >= 3 && selectedStore?.adminId) || user._id}/register`} />}
            <Box sx={{ pt: 3, textAlign: 'center' }}>
              <a
                component={RouterLink}
                rel="noreferrer"
                href={((user.role >= 3 && selectedStore?.adminId) || user._id) ? `${process.env.REACT_APP_CLIENT_URL}/${(user.role >= 3 && selectedStore?.adminId) || user._id}/register` : '#'}
                target="_blank"
                style={{ textDecoration: 'underline', color: 'inherit' }}
              >
                Go to Link
              </a>
            </Box>
          </Card>
          ) : (
            <Card sx={{ pb: 5, pl: 5, pr: 5, mb:3 }}>
            <CardHeader title={user.company || 'Your Company'} sx={{ pl: 0, pb: 0 }} />
            <Box sx={{ pl: 0, pt: 0, pb: 2 }}>
              <Typography variant="body1" gutterBottom sx={{ pl: 0, pt: 0, pb: 2 }}>
                {user._id || 'User ID'}
              </Typography>
            </Box>
            {user._id && <QRCode value={`${process.env.REACT_APP_CLIENT_URL}/${user._id}/register`} />}
            <Box sx={{ pt: 3, textAlign: 'center' }}>
              <a
                component={RouterLink}
                rel="noreferrer"
                href={user._id ? `${process.env.REACT_APP_CLIENT_URL}/${user._id}/register` : '#'}
                target="_blank"
                style={{ textDecoration: 'underline', color: 'inherit' }}
              >
                Go to Link
              </a>
            </Box>
          </Card>
          )}
        </Grid>
        
        {!isLoading && branches.length > 0 ?
          <>
            <Box sx={{ mt: 2, pb: 2 }}>
              <Typography variant="h4">Branches QR Code</Typography>
            </Box>

            <Grid container spacing={3} >
              {branches && branches.map((v, k) => {
                return (
                  <Grid item key={`grid-${k}`} xs={12} sm={6} md={4} lg={3} >
                    <Card sx={{ p:3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <Box>
                        <CardHeader title={v.company} sx={{ pl: 0, pb: 0, pt:0 }} />
                        <Box sx={{ pl: 0, pt: 0, pb: 2 }}>
                          <Typography variant="body1" gutterBottom >
                            {v._id}
                          </Typography>
                        </Box>
                        <Box sx={{textAlign: 'center'}}>
                          <QRCode value={`${process.env.REACT_APP_CLIENT_URL}/${v._id}/register`} size={128} />
                        </Box>
                      </Box>
                      <Box sx={{ pt: 3, textAlign: 'center' }}>
                        <a
                          component={RouterLink}
                          rel="noreferrer"
                          href={`${process.env.REACT_APP_CLIENT_URL}/${v._id}/register`}
                          target="_blank"
                          style={{ textDecoration: 'underline', color: 'inherit' }}
                        >
                          Go to Branch Link
                        </a>
                      </Box>
                    </Card>
                  </Grid>
                )
              })}

            </Grid>
          </> : ""}
          {user.role > 0 && (
            <Box sx={{mt: 5}}>
              <Box sx={{ pb: 1 }}>
                <Typography variant="h4">Schedule and Premium Hours Approval</Typography>
              </Box>
              <Typography variant="body2" sx={{mb:3}}>
                  Manage approvals of schedules, overtime, and night shift differential hours.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6} lg={4}>
                  <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                      Option 1: QR Code Approval
                    </Typography>
                     
                    <Box component="ul" sx={{ textAlign: 'left', mb: 2, typography: 'body2', pl: 2 }}>
                      <li>Scan system-generated QR using the <b>Employee App</b></li>
                      <li>For use by <b>authorized personnel only</b></li>
                      <li>Ideal for fast, on-site approvals</li>
                    </Box>
                    {timeInQR && (
                      <Box sx={{ mb: 3, mt:2 }}>
                        <QRCode value={timeInQR} />
                      </Box>
                    )}
                     
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={openOtpModalForQRGeneration}
                      disabled={isQrGenerating || isLoading}
                      sx={timeInQR ? { mt: 0 } : { mt: 2 }}
                    >
                      {isQrGenerating ? 'Generating QR...' : 'Generate New Schedule/Premium QR Code'}
                    </Button>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                    <Typography variant="h6" gutterBottom>
                      Option 2: OTP Approval
                    </Typography>
                    <Box component="ul" sx={{ textAlign: 'left', mb: 2, typography: 'body2', pl: 2 }}>
                      <li>Generate a <b>6-digit OTP</b> (valid for 24 hours)</li>
                      <li>Use OTP to confirm and approve requests</li>
                      <li>Recommended for remote or time-based approvals</li>
                    </Box>
                    {internalOtp && (
                      <Box sx={{ mt: 2, p:2, border: '1px dashed grey', borderRadius: 1 }}>
                        <Typography variant="body1" component="p" sx={{mb:0.5}}>
                          This OTP is valid for 24 hours only.
                        </Typography>
                        <Typography variant="h5" component="p" sx={{fontWeight: 'bold', letterSpacing: 2, color: 'primary.main'}}>
                           {internalOtp}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{mt:0.5}}>
                          Valid Until: {internalOtpValidUntil}
                        </Typography>
                      </Box>
                    )}
                    <Button 
                     variant="contained" 
                     color="secondary"
                     onClick={handleGenerateInternalOTP}
                     disabled={isInternalOtpLoading || isLoading}
                     sx={{ mt: 2 }}
                    >
                      {isInternalOtpLoading ? 'Generating OTP...' : 'Generate 24-Hour OTP'}
                    </Button>
                  </Card>
                </Grid>
              </Grid>
              <Typography variant="caption" display="block" sx={{mt:2}}>
                ⚠️<i>This QR code and OTP is used by authorized personnel to approve employee-submitted schedules, overtime, and night shift differential hours via the Employee App</i>.
              </Typography>
            </Box>
          )}
      </Container>
      </Page >

      {/* OTP Input Modal */}
      <Modal open={openOTPDialog} onClose={handleCloseOTPDialog}>
        <Box sx={style}>
          <Typography variant="h6" component="h2">
            Enter OTP from Email
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
            Please enter the 6-digit OTP sent to your registered email address. This OTP is valid for 5 minutes.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
            <OTPInput
              value={otpFromModal}
              onChange={setOtpFromModal}
              numInputs={6}
              inputType="number"
              shouldAutoFocus
              containerStyle={{ display: "flex", gap: "8px" }}
              inputStyle={{
                width: "calc(100% / 6 - 8px)", // Responsive width
                maxWidth: "40px", // Max width for each input
                height: "40px",
                fontSize: "1.5rem",
                borderRadius: "8px",
                border: "1px solid #ccc",
                textAlign: "center",
                outline: "none",
                background: "#fff",
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.1)",
              }}
              renderInput={(props) => <input {...props} />}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 2 }}>
            <Button
              variant="text"
              color="primary"
              onClick={handleRequestEmailOtpForQR}
              disabled={isSendingEmailOtp || resendCooldown > 0}
            >
              {isSendingEmailOtp ? 'Sending OTP...' : resendCooldown > 0 ? `Resend OTP (${resendCooldown}s)` : 'Resend OTP'}
            </Button>
          </Box>
          <Box mt={3} display="flex" width={'100%'} justifyContent="space-between">
            <Button variant="outlined" color="primary" onClick={handleCloseOTPDialog}>
              Cancel
            </Button>
            <Button variant="contained" color="success" onClick={handleModalSubmitAndGenerateQR} disabled={isQrGenerating || otpFromModal.length !== 6}>
              {isQrGenerating ? 'Verifying & Generating...' : 'Submit & Generate QR'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  )
}
